import {useEffect, useId, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {ArrowRight, ChevronDown} from 'lucide-react';
import './ClientInstallInstructions.css';
import {authDestination} from './authContinuation';
import {availableClientTools,installCommand,installPrompt,localInitPrompt,scriptInstallCommand} from './clientTools';
import {InstallDisclosure} from './InstallDisclosure';
import {RemoteClientEntry} from './RemoteClientSetup';
import {REMOTE_INIT_PROMPT} from './remoteInstallation';
import {CopyableCommand} from './CopyableCommand';
import ClientToolPicker from './ClientToolPicker';
import {NativePluginEntry,NATIVE_INIT_PROMPT} from './NativePluginSetup';
export {CopyableCommand} from './CopyableCommand';
export {InstallDisclosure} from './InstallDisclosure';

export const clientInstallAvailable=config=>!!config?.enabled && (!!config.package_ready || !!config.remote_clients?.length || !!config.native_clients?.length);

// null while loading; {enabled:false} when the server can't be asked.
export function useClientInstallConfig(enabled = true) {
  const [config, setConfig] = useState(null);
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    fetch('/api/client-setup/config', {signal: controller.signal})
      .then(response => response.ok ? response.json() : {enabled: false})
      .then(value => setConfig(value))
      .catch(error => { if (error.name !== 'AbortError') setConfig({enabled: false}); });
    return () => controller.abort();
  }, [enabled]);
  return config;
}

const TITLES = ['Install Pabs in your AI tool', 'Initialize your project', 'Keep Pabs in the loop'];
export function supportedClientNames(config) {
  const {tools}=availableClientTools(config);
  return new Intl.ListFormat('en',{style:'long',type:'conjunction'}).format(tools.map(tool=>tool.label));
}
export function ClientCompatibility({config}) {
  return <div className="pabs-install-compatibility">
    <p>{supportedClientNames(config) || 'Choose your AI tool below'}. Choose your tool to see its setup.</p>
    <p>Choose your tool, then follow its install prompt. Already using Pabs? Choose your existing product during setup.</p>
  </div>;
}

// Public install guide (homepage and docs). Signed-in /brief uses BriefClientSetup.
export default function ClientInstallInstructions({config: providedConfig, heading = true, appearance = 'auto', layout = 'stacked'}) {
  const discovered = useClientInstallConfig(providedConfig === undefined);
  const config = providedConfig || discovered;
  const [clientChoice, setClientChoice] = useState(()=>new URLSearchParams(window.location.search).get('client') || 'codex');
  const [step, setStep] = useState(0);
  const [terminal, setTerminal] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [windows, setWindows] = useState(() => /Win/i.test(navigator.platform));
  const id = useId();
  const headers = useRef([]);
  // Signed-in visitors already have an account.
  const signedIn = (() => { try { return !!localStorage.getItem('token'); } catch { return false; } })();
  if (!clientInstallAvailable(config)) return null;
  const {tools:clients}=availableClientTools(config);
  const tool=clients.find(item=>item.id===clientChoice) || clients[0];
  if (!tool) return null;
  const client=tool.id;
  const desktop = client === 'claude-desktop';
  const remote=tool.host;
  const native=tool.native;
  const titles = TITLES.map((title,index)=>index===0 && remote ? `Set up Pabs in ${tool.label}` : title);
  const command = installCommand({client,windows,testMode});
  const npm = !testMode;
  const useTerminal = terminal || desktop;
  const installText = useTerminal ? command : installPrompt(tool, command);
  const initText = native ? NATIVE_INIT_PROMPT : remote ? REMOTE_INIT_PROMPT : localInitPrompt(client);
  function next(index) {
    setStep(index);
    headers.current[index]?.focus();
  }
  return <div className={`pabs-install${layout === 'feature' ? ' pabs-install-feature' : ''}`} data-appearance={appearance}>
    {heading && <div className="pabs-install-intro">
        <h2>Bring Pabs into your project</h2>
        <p>Your product direction, decisions and design standards, alongside your AI&nbsp;client.</p>
      </div>}
    <div className="pabs-install-steps">
      {titles.map((title, index) => <section className="pabs-install-step" key={index}>
        <h3>
          <button type="button" ref={element => { headers.current[index] = element; }} id={`${id}-step-${index}`} aria-expanded={step === index} aria-controls={`${id}-content-${index}`} onClick={() => setStep(step === index ? null : index)}>
            <span className="pabs-install-number" aria-hidden="true">{index + 1}</span>
            <span>{title}</span>
            <ChevronDown size={19} className="pabs-install-chevron" aria-hidden="true"/>
          </button>
        </h3>
        <InstallDisclosure open={step === index} id={`${id}-content-${index}`} labelledBy={`${id}-step-${index}`}>
        <div className="pabs-install-step-body">
          {index === 0 && <>
            {!remote && !signedIn && <p className="pabs-install-account">You’ll need a free Pabs account. <Link to={authDestination('/register', '/docs/install-pabs')}>Create account</Link> or <Link to={authDestination('/login', '/docs/install-pabs')}>sign in</Link>; connect it during setup.</p>}
            <ClientToolPicker tools={clients} selected={client} onSelect={setClientChoice}/>
            {native ? <><NativePluginEntry tool={tool}/><button type="button" className="pabs-install-next" onClick={() => next(1)}>Next: set up your project <ArrowRight size={18} aria-hidden="true"/></button></> : remote ? <RemoteClientEntry host={remote}/> : <>
            <p>{useTerminal ? 'Run in your project folder.' : 'Paste this into your agent’s chat.'}</p>
            <CopyableCommand key={installText} value={installText} label={useTerminal ? 'Copy install command' : 'Copy install prompt'}/>
            <details className="pabs-install-options">
              <summary>Requirements & other ways to install</summary>
              {npm ? <>
                <p>Node.js 18+ with npm is required. Run the same command again to update.</p>
                <p>No npm? Run <code>{scriptInstallCommand({client})}</code> instead, or <code>{scriptInstallCommand({client,windows:true})}</code> in Windows PowerShell.</p>
              </> : <p>Node.js 18+ and tar are required. Run the same command again to update.</p>}
              {!desktop && <button type="button" className="pabs-install-text-button" onClick={() => setTerminal(!terminal)}>{terminal ? 'Use an install prompt instead' : 'Use a terminal instead'}</button>}
              {!npm && <div className="pabs-install-clients" role="group" aria-label="Terminal platform">
                <button type="button" aria-pressed={!windows} onClick={() => setWindows(false)}>macOS / Linux</button>
                <button type="button" aria-pressed={windows} onClick={() => setWindows(true)}>Windows PowerShell</button>
              </div>}
              {desktop && <p>Desktop connects to this project through MCP. Its tools can read project documents; coding and testing depend on your client’s other tools.</p>}
              {config.local_preview && <label className="pabs-install-test-option"><input type="checkbox" checked={testMode} onChange={event => setTestMode(event.target.checked)}/> Include the temporary test reset skill</label>}
              {config.local_preview && <p>This preview connects to {window.location.origin}. Test reset requires the local test server.</p>}
            </details>
            <button type="button" className="pabs-install-next" onClick={() => next(1)}>Next: set up your project <ArrowRight size={18} aria-hidden="true"/></button>
            </>}
          </>}
          {index === 1 && <>
            <p>{native ? 'After installing Pabs, ask:' : remote ? `After adding the link in ${remote.label}, ask in your project chat:` : desktop ? 'Restart Claude Desktop, then ask:' : 'Reload your tool, then ask:'}</p>
            <CopyableCommand key={initText} value={initText} label="Copy initialization prompt"/>
            <p>{native ? 'Connect your Pabs account when asked. Pabs picks up your existing product or helps set up a new one.' : remote ? `${remote.label} reads your project and sets up your Brief and strategy in Pabs, or picks up the product you chose. It only asks for what’s missing.` : 'Your tool shows a Pabs approval link and a confirmation code. Sign in, check the code matches, and choose a new product or one you already have. Pabs then sets up your Brief and strategy, asking only for what’s missing.'}</p>
            <button type="button" className="pabs-install-next" onClick={() => next(2)}>Next: work with Pabs <ArrowRight size={18} aria-hidden="true"/></button>
          </>}
          {index === 2 && <>
            <p>Keep building with your client. Pabs helps you choose what’s next, capture learnings and review work against your product’s direction.</p>
            <CopyableCommand value="Use Pabs to help me choose the next thing to build." label="Copy first task prompt"/>
            <p>The client returns consequential progress to the saved work and explains Pabs’s response, including any choice still needed from you. Routine edits use proportionate local checks; background work runs when explicitly scheduled.</p>
            <Link to="/docs/install-pabs" className="pabs-install-next">Explore setup and usage <ArrowRight size={18} aria-hidden="true"/></Link>
          </>}
        </div>
        </InstallDisclosure>
      </section>)}
    </div>
  </div>;
}
